import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, KeyRound, Plus, Pencil, Trash2, X, UserCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { AdminPhoneActions } from "../../components/AdminPhoneActions";
import { PhoneField } from "../../components/PhoneField";
import {
  buildPhoneFieldValue,
  formatPhoneForDisplay,
  getPhoneFieldError,
  touchPhoneField,
} from "../../lib/phone";

const DAYS = [
  { v: 0, label: "Mon" },
  { v: 1, label: "Tue" },
  { v: 2, label: "Wed" },
  { v: 3, label: "Thu" },
  { v: 4, label: "Fri" },
  { v: 5, label: "Sat" },
  { v: 6, label: "Sun" },
];

export default function AdminDrivers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [credentialFlash, setCredentialFlash] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/drivers");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load drivers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleActive = async (d) => {
    try {
      await api.put(`/admin/drivers/${d.id}`, { is_active: !d.is_active });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete driver "${d.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/drivers/${d.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const save = async (form) => {
    setSaving(true);
    try {
      if (editing && editing.id) {
        await api.put(`/admin/drivers/${editing.id}`, form);
        toast.success("Driver updated");
      } else {
        const { data } = await api.post("/admin/drivers", form);
        if (data?.temporary_password) {
          setCredentialFlash({
            driverName: data.driver?.name || form.name,
            temporaryPassword: data.temporary_password,
            expiresAt: data.temporary_password_expires_at,
          });
        }
        toast.success("Driver created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async (d) => {
    if (!window.confirm(`Generate a new temporary password for ${d.name}?`)) return;
    try {
      const { data } = await api.post(`/admin/drivers/${d.id}/reset-password`);
      setCredentialFlash({
        driverName: d.name,
        temporaryPassword: data.temporary_password,
        expiresAt: data.temporary_password_expires_at,
      });
      toast.success("Temporary password reset");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const copyPassword = async () => {
    if (!credentialFlash?.temporaryPassword) return;
    try {
      await navigator.clipboard.writeText(credentialFlash.temporaryPassword);
      toast.success("Temporary password copied");
    } catch {
      toast.error("Could not copy password");
    }
  };

  return (
    <div data-testid="admin-drivers-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Drivers</h1>
          <p className="text-sm text-slate-500">
            Manage your driver roster, working hours, and unavailable dates.
          </p>
        </div>
        <button
          onClick={() => setEditing({})}
          data-testid="drv-new-btn"
          className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
        >
          <Plus size={14} /> New driver
        </button>
      </div>

      {credentialFlash && (
        <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="driver-temp-password-box">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-800">Temporary password</p>
          <p className="mt-2 text-sm text-slate-700">This temporary password is shown only once for <strong>{credentialFlash.driverName}</strong>.</p>
          <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <code className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-900 ring-1 ring-inset ring-amber-200" data-testid="driver-temp-password-value">{credentialFlash.temporaryPassword}</code>
            <button type="button" onClick={copyPassword} data-testid="driver-temp-password-copy" className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <Copy size={14} /> Copy
            </button>
          </div>
          {credentialFlash.expiresAt && <p className="mt-2 text-xs text-slate-500">Expires: {new Date(credentialFlash.expiresAt).toLocaleString()}</p>}
        </div>
      )}

      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Working days</th>
                <th className="px-4 py-3">Hours</th>
                <th className="px-4 py-3">Active</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-500">No drivers yet</td></tr>
              )}
              {!loading && rows.map((d) => (
                <tr key={d.id} data-testid={`drv-row-${d.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block w-3 h-3 rounded-full shrink-0"
                        style={{ backgroundColor: d.color || "#94a3b8" }}
                      />
                      <div>
                        <p className="font-medium text-slate-900">{d.name}</p>
                        {d.home_base && <p className="text-xs text-slate-500">{d.home_base}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {d.email && <p>{d.email}</p>}
                    {d.phone && <p>{formatPhoneForDisplay(d.phone)}</p>}
                    <AdminPhoneActions phone={d.phone} testIdPrefix={`driver-phone-${d.id}`} />
                    {!d.email && !d.phone && <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {d.working_days && d.working_days.length > 0
                      ? d.working_days.map((wd) => DAYS[wd]?.label).join(" ")
                      : <span className="text-slate-400">all</span>}
                  </td>
                  <td className="px-4 py-3 text-xs tabular-nums">
                    {d.work_start_time || "00:00"} – {d.work_end_time || "23:59"}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleActive(d)}
                      data-testid={`drv-toggle-active-${d.id}`}
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${
                        d.is_active
                          ? "bg-emerald-100 text-emerald-900 ring-emerald-200"
                          : "bg-slate-100 text-slate-600 ring-slate-200"
                      }`}
                    >
                      {d.is_active ? "Active" : "Disabled"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => resetPassword(d)}
                      data-testid={`drv-reset-password-${d.id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-900 mr-3"
                    >
                      <KeyRound size={12} /> Reset password
                    </button>
                    <button
                      onClick={() => setEditing(d)}
                      data-testid={`drv-edit-${d.id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-900 mr-3"
                    >
                      <Pencil size={12} /> Edit
                    </button>
                    <button
                      onClick={() => remove(d)}
                      data-testid={`drv-delete-${d.id}`}
                      className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <DriverEditor
          initial={editing}
          onCancel={() => setEditing(null)}
          onSave={save}
          saving={saving}
        />
      )}
    </div>
  );
}

function DriverEditor({ initial, onCancel, onSave, saving }) {
  const isEdit = !!initial.id;
  const [form, setForm] = useState({
    name: initial.name || "",
    email: initial.email || "",
    phone: buildPhoneFieldValue(initial.phone),
    is_active: initial.is_active !== undefined ? initial.is_active : true,
    working_days: initial.working_days || [0, 1, 2, 3, 4, 5, 6],
    work_start_time: initial.work_start_time || "08:00",
    work_end_time: initial.work_end_time || "20:00",
    manual_unavailable_dates: (initial.manual_unavailable_dates || []).join(", "),
    max_daily_hours: initial.max_daily_hours ?? "",
    notes: initial.notes || "",
    home_base: initial.home_base || "",
    color: initial.color || "#1e5c9a",
  });

  const toggleDay = (v) => {
    const curr = new Set(form.working_days);
    if (curr.has(v)) curr.delete(v); else curr.add(v);
    setForm({ ...form, working_days: Array.from(curr).sort((a, b) => a - b) });
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    const phoneError = getPhoneFieldError(form.phone);
    if (phoneError) {
      setForm({ ...form, phone: touchPhoneField(form.phone) });
      toast.error(phoneError);
      return;
    }
    const dates = form.manual_unavailable_dates
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    onSave({
      name: form.name.trim(),
      email: form.email || null,
      phone: form.phone.e164 || null,
      phone_country: form.phone.country || null,
      is_active: !!form.is_active,
      working_days: form.working_days,
      work_start_time: form.work_start_time || null,
      work_end_time: form.work_end_time || null,
      manual_unavailable_dates: dates,
      max_daily_hours: form.max_daily_hours === "" ? null : Number(form.max_daily_hours),
      notes: form.notes || null,
      home_base: form.home_base || null,
      color: form.color || null,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/60 p-0 sm:p-6"
      onClick={onCancel}
      data-testid="drv-editor-dialog"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-t-2xl sm:rounded-xl bg-white p-6 space-y-4 max-h-[92vh] overflow-auto"
      >
        <div className="flex items-start justify-between">
          <h3 className="font-heading text-xl font-semibold flex items-center gap-2">
            <UserCircle2 size={18} /> {isEdit ? "Edit driver" : "New driver"}
          </h3>
          <button type="button" onClick={onCancel} aria-label="close" className="text-slate-500">
            <X size={18} />
          </button>
        </div>

        <Labeled label="Name *">
          <input required data-testid="drv-field-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" />
        </Labeled>

        <div className="grid gap-3 sm:grid-cols-2">
          <Labeled label="Email">
            <input type="email" data-testid="drv-field-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" />
          </Labeled>
          <Labeled label="Phone">
            <PhoneField
              value={form.phone}
              onChange={(phone) => setForm({ ...form, phone })}
              countryTestId="drv-field-phone-country"
              numberTestId="drv-field-phone"
              errorTestId="drv-field-phone-error"
            />
          </Labeled>
        </div>

        <Labeled label="Working days">
          <div className="flex flex-wrap gap-1.5">
            {DAYS.map((d) => {
              const on = form.working_days.includes(d.v);
              return (
                <button
                  key={d.v}
                  type="button"
                  onClick={() => toggleDay(d.v)}
                  data-testid={`drv-day-${d.v}`}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${
                    on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"
                  }`}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        </Labeled>

        <div className="grid gap-3 sm:grid-cols-2">
          <Labeled label="Work start">
            <input type="time" data-testid="drv-field-start" value={form.work_start_time} onChange={(e) => setForm({ ...form, work_start_time: e.target.value })}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" />
          </Labeled>
          <Labeled label="Work end">
            <input type="time" data-testid="drv-field-end" value={form.work_end_time} onChange={(e) => setForm({ ...form, work_end_time: e.target.value })}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" />
          </Labeled>
        </div>

        <Labeled label="Unavailable dates (comma-separated YYYY-MM-DD)">
          <input data-testid="drv-field-blocked" value={form.manual_unavailable_dates}
            onChange={(e) => setForm({ ...form, manual_unavailable_dates: e.target.value })}
            placeholder="2026-07-01, 2026-07-15"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" />
        </Labeled>

        <div className="grid gap-3 sm:grid-cols-3">
          <Labeled label="Max daily hrs">
            <input type="number" min="1" max="24" data-testid="drv-field-maxh" value={form.max_daily_hours}
              onChange={(e) => setForm({ ...form, max_daily_hours: e.target.value })}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" />
          </Labeled>
          <Labeled label="Home base">
            <input data-testid="drv-field-base" value={form.home_base} onChange={(e) => setForm({ ...form, home_base: e.target.value })}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" />
          </Labeled>
          <Labeled label="Color">
            <input type="color" data-testid="drv-field-color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}
              className="w-full h-[38px] rounded-md border border-slate-300" />
          </Labeled>
        </div>

        <Labeled label="Internal notes">
          <textarea rows={2} data-testid="drv-field-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" />
        </Labeled>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" data-testid="drv-field-active" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900" />
          Active
        </label>

        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <button type="submit" disabled={saving} data-testid="drv-editor-save"
            className="flex-1 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create driver"}
          </button>
          <button type="button" onClick={onCancel}
            className="flex-1 rounded-md border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function Labeled({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
